import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { signOut, useSession } from 'next-auth/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';

import { useAuth, useToast } from '~/contexts';
import { useCanProxy } from '~/features/proxy/hooks/useCanProxy';
import { User } from '~/shared/auth/useUser';
import { Icon } from '~/shared/components/Icon';
import { useGetProfilePicture } from '~/shared/hooks';
import { USER_ABILITIES_QUERY_KEY } from '~/shared/hooks/useUserAbilities';
import { isProduction } from '~/shared/utils/environments';
import { assignLocation } from '~/shared/utils/fullPageNavigation';

type UseUserDropdownProps = {
  user: User;
};

export const useUserDropdown = ({ user }: UseUserDropdownProps) => {
  const { t } = useTranslate();
  const router = useRouter();
  const { data: session, update } = useSession();
  const { notifyChangedProxy } = useAuth();
  const canProxy = useCanProxy();
  const toast = useToast();
  const queryClient = useQueryClient();

  async function handleSignOut() {
    try {
      await signOut({ redirect: false });

      const tenantId = process.env.NEXT_PUBLIC_AZURE_AD_TENANT_ID;
      const microsoftLogoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`;

      window.location.href = microsoftLogoutUrl;
    } catch (error) {
      console.error('Error on logout:', error);
    }
  }

  const handleStopProxy = useCallback(async () => {
    if (!session?.user) return;

    const updatedSession = await update({ action: 'proxy-out' });

    if (updatedSession && !updatedSession.user.px) {
      notifyChangedProxy();
      assignLocation('/home');
      return;
    }

    toast.add({ type: 'error', description: t('common.default.error') });
  }, [notifyChangedProxy, session?.user, t, toast, update]);

  const handleForceRefreshAbilities = useCallback(async () => {
    try {
      const currentUser = session?.user;
      if (!currentUser) return;
      toast.add({
        type: 'neutral',
        description: t('user_dropdown.refreshing_abilities'),
      });
      await update();
      await queryClient.invalidateQueries({
        queryKey: USER_ABILITIES_QUERY_KEY,
      });
      toast.add({
        type: 'success',
        description: t('user_dropdown.abilities_refreshed'),
      });
    } catch (error) {
      toast.add({
        type: 'error',
        description: t('user_dropdown.error_refreshing_abilities'),
      });
    }
  }, [queryClient, session?.user, t, toast, update]);

  const items = useMemo(() => {
    const dropdownItems = [];

    if (user.isProxied) {
      dropdownItems.push({
        label: t('proxy.banner.stop_proxy'),
        onSelect: handleStopProxy,
        icon: <Icon icon="signOut" size={16} isCustom />,
      });
    } else if (canProxy) {
      dropdownItems.push({
        label: t('common.proxy_as_user'),
        onSelect: async () => {
          router.push('/proxy');
        },
        icon: <Icon icon="UserRound" size={16} />,
      });
    }

    if (!isProduction()) {
      dropdownItems.push({
        label: t('common.refresh_abilities'),
        onSelect: handleForceRefreshAbilities,
        icon: <Icon icon="RefreshCcwDot" size={16} />,
      });
    }

    dropdownItems.push({
      label: t('common.sign_out'),
      onSelect: handleSignOut,
      icon: <Icon icon="signOut" size={16} isCustom />,
    });

    return dropdownItems;
  }, [
    canProxy,
    handleForceRefreshAbilities,
    handleStopProxy,
    router,
    t,
    user.isProxied,
  ]);

  const { data } = useGetProfilePicture(user.globalId ?? '');
  const imageSrc = data ? URL.createObjectURL(data) : undefined;

  const profileInfo = useMemo(
    () => ({
      name: user.name ?? '',
      urlImage: imageSrc,
      zone: user.zone ?? '',
      teamName: user.currentTeamName ?? '',
      isApprover: user.isApprover ?? false,
    }),
    [imageSrc, user.currentTeamName, user.isApprover, user.name, user.zone]
  );

  return {
    items,
    profileInfo,
  };
};
